/**
 * Uploading one checklist picture.
 *
 * The whole path in one function, with no Uppy: the file goes to GCS exactly as
 * the camera produced it, and nothing is derived from it on the way.
 *
 * Why the file is not downscaled: these photos are the quality record of a
 * panel, looked at later precisely to see something small -- a joint, a label, a
 * defect, a measurement. Measured on production, the median checklist photo
 * today is 84 KB, because the Uppy webcam read a `getUserMedia` video track
 * instead of the camera sensor. Fixing that is the point of the change, so
 * nothing here may throw pixels away to save seconds.
 *
 * It does build a **thumbnail**, but only to put it in GCS beside the original,
 * never in the database: `cloudstorage/thumbs.py` says why. Doing it here is
 * free -- the picture is already in the browser's hands -- and it saves the
 * server downloading a full-resolution photo back out of the bucket later just
 * to shrink it. It is best effort: a photo whose thumbnail fails still uploads,
 * and the server builds one the first time anybody looks at it.
 */
import { backend_api_v2 } from '/src/utils/apiv2'

// Matches THUMB_MAX_EDGE / THUMB_QUALITY in cloudstorage/thumbs.py, so a
// thumbnail looks the same whichever side made it.
const THUMB_MAX_EDGE = 400
const THUMB_QUALITY = 0.72

/** A small JPEG of `file`, or null when the browser cannot decode it. */
async function buildThumb (file) {
  let bitmap = null
  try {
    bitmap = await createImageBitmap(file)
    const scale = Math.min(1, THUMB_MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const w = Math.max(1, Math.round(bitmap.width * scale))
    const h = Math.max(1, Math.round(bitmap.height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
    return await new Promise(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', THUMB_QUALITY))
  } catch (e) {
    return null
  } finally {
    if (bitmap && bitmap.close) bitmap.close()
  }
}

/** A name of our own, never the camera's.
 *
 * `clean_files_by_name` deletes every file in a package sharing a name, and the
 * failure path used to call it -- measured on production, 19 056 (package, name)
 * pairs already hold more than one file. A name built from the task and the
 * clock cannot collide with another task's. */
function pictureName (meta, file) {
  const ext = (String(file?.name || '').match(/\.([A-Za-z0-9]+)$/)?.[1] || 'jpg').toLowerCase()
  const slug = String(meta?.text || 'photo')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'photo'
  const stamp = new Date().toISOString().replace(/[:.]/g, '-')
  const salt = Math.random().toString(36).slice(2, 6)
  return `${slug}-${stamp}-${salt}.${ext}`
}

async function putFile (target, file, type) {
  const response = await fetch(target.url, {
    method: target.method || 'PUT',
    // The signed URL binds Content-Type, so these have to be the headers the
    // presign was asked for, not headers of our own.
    headers: target.headers || { 'Content-Type': type },
    body: file,
  })
  if (!response.ok) {
    throw new Error(`PUT failed with ${response.status}`)
  }
}

/**
 * Presign, PUT, and clean up after ourselves if the PUT fails.
 *
 * `meta` carries only the three keys a task is matched by. The uploader is
 * stamped server-side by `presign_for_new_file` (`user` / `employees`) and must
 * not be sent from here: a client that can name the authors can name the wrong
 * ones.
 *
 * Returns the created CloudFile, which the presign call now answers with, so
 * the caller can show the file without refetching the package.
 */
export async function uploadPicture ({ pck_uuid, file, meta, deleteFile }) {
  const type = file.type || 'image/jpeg'

  const { data } = await backend_api_v2.post(
    `/cloudstorage/packages/${pck_uuid}/presign_for_new_file/`,
    { name: pictureName(meta, file), type, meta },
  )

  const cloud_file = data?.file || null
  try {
    await putFile(data, file, type)
  } catch (first) {
    // One retry: a single PUT has no resumable session, so a dropped
    // connection restarts from zero and a second attempt is the cheapest
    // thing that helps.
    try {
      await putFile(data, file, type)
    } catch (second) {
      // The presign already created the row. Leaving it behind would show the
      // operator a file that is not there, so it goes -- by uuid, never by
      // name.
      if (cloud_file?.uuid && deleteFile) {
        try { await deleteFile(cloud_file.uuid) } catch (e) { /* best effort */ }
      }
      throw second
    }
  }

  // The thumbnail rides after the picture, never before it: a panel with a
  // thumbnail and no photo would be a lie, and one with a photo and no
  // thumbnail is just a plain tile until somebody opens the panel again.
  if (data.thumb) {
    try {
      const thumb = await buildThumb(file)
      if (thumb) await putFile(data.thumb, thumb, 'image/jpeg')
    } catch (e) {
      // Nothing to do and nothing to say: the server builds it on demand.
    }
  }

  return cloud_file
}
